import React from 'react';
import { createBrowserRouter } from 'react-router-dom';
import PrivateRoute from './components/common/PrivateRoute';
import ProtectedLoginRoute from './components/common/ProtectedLoginRoute';
import DashboardLayout from './components/layout/DashboardLayout';
import PublicLayout from './components/layout/PublicLayout';
import LoginForm from './components/auth/LoginForm';
import RegisterForm from './components/auth/RegisterForm';
import CommandTemplates from './components/templates/CommandTemplates';
import Dashboard from './pages/Dashboard';
import Conversations from './pages/Conversations';
import Products from './pages/Products';
import Settings from './pages/Settings';
import Analytics from './pages/Analytics';
import Notifications from './pages/Notifications';
import Orders from './pages/Orders';
import OrderDetails from './components/orders/OrderDetails';
import UnresponsiveCustomers from './pages/UnresponsiveCustomers';
import DeliveryPersons from './pages/DeliveryPersons';
import DeliveryPersonForm from './pages/DeliveryPersons/DeliveryPersonForm';
import OrderAssignment from './pages/OrderAssignment';
import Cashiers from './pages/Cashiers';
import Clients from './pages/Clients';
import Profile from './pages/Profile';
import NotFound from './pages/NotFound';
import Shops from './pages/ShopManagement';

// Nouvelles pages publiques
import LandingPage from './pages/LandingPage';
import Pricing from './pages/Pricing';
import About from './pages/About';
import Features from './pages/Features';
import Contact from './pages/Contact';

// Composant pour gérer la redirection intelligente de la route racine
const RootRoute = () => {
  const [session, setSession] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  
  React.useEffect(() => {
    import('./utils/supabase').then(({ supabase }) => {
      supabase.auth.getSession().then(({ data: { session } }) => {
        setSession(session);
        setLoading(false);
      });

      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((_event, session) => {
        setSession(session);
        setLoading(false);
      });

      return () => subscription.unsubscribe();
    });
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-cyan-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Si connecté, rediriger vers le dashboard immédiatement
  if (session) {
    window.location.href = '/app/dashboard';
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-cyan-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Si non connecté, afficher la landing page avec PublicLayout
  return (
    <PublicLayout>
      <LandingPage />
    </PublicLayout>
  );
};

const router = createBrowserRouter([
  // Route racine indépendante pour éviter le flash
  {
    path: '/',
    element: <RootRoute />,
  },
  // Routes publiques avec PublicLayout
  {
    path: '/public',
    element: <PublicLayout />,
    children: [
      {
        path: 'about',
        element: <About />,
      },
      {
        path: 'features',
        element: <Features />,
      },
      {
        path: 'contact',
        element: <Contact />,
      },
      {
        path: 'login',
        element: <ProtectedLoginRoute><LoginForm /></ProtectedLoginRoute>,
      },
      {
        path: 'register',
        element: <ProtectedLoginRoute><RegisterForm /></ProtectedLoginRoute>,
      },
    ],
  },
  // Routes publiques directes pour compatibilité
  {
    path: '/about',
    element: <PublicLayout><About /></PublicLayout>,
  },
  {
    path: '/features',
    element: <PublicLayout><Features /></PublicLayout>,
  },
  {
    path: '/contact',
    element: <PublicLayout><Contact /></PublicLayout>,
  },
  {
    path: '/login',
    element: <PublicLayout><ProtectedLoginRoute><LoginForm /></ProtectedLoginRoute></PublicLayout>,
  },
  {
    path: '/register',
    element: <PublicLayout><ProtectedLoginRoute><RegisterForm /></ProtectedLoginRoute></PublicLayout>,
  },
  // Page Pricing indépendante avec son propre header
  {
    path: '/pricing',
    element: <Pricing />,
  },
  // Routes privées avec DashboardLayout
  {
    path: '/app',
    element: <DashboardLayout />,
    children: [
      {
        path: '',
        element: <PrivateRoute><Dashboard /></PrivateRoute>,
      },
      {
        path: 'dashboard',
        element: <PrivateRoute><Dashboard /></PrivateRoute>,
      },
      {
        path: 'orders',
        element: <PrivateRoute><Orders /></PrivateRoute>,
      },
      {
        path: 'orders/:id',
        element: <PrivateRoute><OrderDetails /></PrivateRoute>,
      },
      {
        path: 'products',
        element: <PrivateRoute><Products /></PrivateRoute>,
      },
      {
        path: 'settings',
        element: <PrivateRoute><Settings /></PrivateRoute>,
      },
      {
        path: 'analytics',
        element: <PrivateRoute><Analytics /></PrivateRoute>,
      },
      {
        path: 'notifications',
        element: <PrivateRoute><Notifications /></PrivateRoute>,
      },
      {
        path: 'unresponsive-customers',
        element: <PrivateRoute><UnresponsiveCustomers /></PrivateRoute>,
      },
      {
        path: 'templates',
        element: <PrivateRoute><CommandTemplates /></PrivateRoute>,
      },
      {
        path: 'delivery-persons',
        element: <PrivateRoute><DeliveryPersons /></PrivateRoute>,
      },
      {
        path: 'delivery-persons/:id',
        element: <PrivateRoute><DeliveryPersonForm /></PrivateRoute>,
      },
      {
        path: 'delivery-persons-orders',
        element: <PrivateRoute><OrderAssignment /></PrivateRoute>,
      },
      {
        path: 'cashiers',
        element: <PrivateRoute><Cashiers /></PrivateRoute>,
      },
      {
        path: 'shops',
        element: <PrivateRoute><Shops /></PrivateRoute>,
      },
      {
        path: 'clients',
        element: <PrivateRoute><Clients /></PrivateRoute>,
      },
      {
        path: 'profile',
        element: <PrivateRoute><Profile /></PrivateRoute>,
      },
      {
        path: '*',
        element: <NotFound />,
      },
    ],
  },
  // Redirection des anciennes routes vers les nouvelles
  {
    path: '/dashboard',
    element: <PrivateRoute redirectTo="/app/dashboard"><></></PrivateRoute>,
  },
  {
    path: '/orders',
    element: <PrivateRoute redirectTo="/app/orders"><></></PrivateRoute>,
  },
  {
    path: '/products',
    element: <PrivateRoute redirectTo="/app/products"><></></PrivateRoute>,
  },
  {
    path: '/settings',
    element: <PrivateRoute redirectTo="/app/settings"><></></PrivateRoute>,
  },
  {
    path: '/analytics',
    element: <PrivateRoute redirectTo="/app/analytics"><></></PrivateRoute>,
  },
  {
    path: '/notifications',
    element: <PrivateRoute redirectTo="/app/notifications"><></></PrivateRoute>,
  },
  {
    path: '/unresponsive-customers',
    element: <PrivateRoute redirectTo="/app/unresponsive-customers"><></></PrivateRoute>,
  },
  {
    path: '/templates',
    element: <PrivateRoute redirectTo="/app/templates"><></></PrivateRoute>,
  },
  {
    path: '/delivery-persons',
    element: <PrivateRoute redirectTo="/app/delivery-persons"><></></PrivateRoute>,
  },
  {
    path: '/delivery-persons-orders',
    element: <PrivateRoute redirectTo="/app/delivery-persons-orders"><></></PrivateRoute>,
  },
  {
    path: '/cashiers',
    element: <PrivateRoute redirectTo="/app/cashiers"><></></PrivateRoute>,
  },
  {
    path: '/shops',
    element: <PrivateRoute redirectTo="/app/shops"><></></PrivateRoute>,
  },
  {
    path: '/clients',
    element: <PrivateRoute redirectTo="/app/clients"><></></PrivateRoute>,
  },
  {
    path: '/profile',
    element: <PrivateRoute redirectTo="/app/profile"><></></PrivateRoute>,
  },
  // Gestion des 404
  {
    path: '*',
    element: <NotFound />,
  },
]);

export default router; 